import * as React from 'react';
import { observer } from 'mobx-react';
import PayText from 'components/Common/payText';
import { AmountCode, StockDisplayType } from 'typings/enums';
import { PayHistoryChangeEntry } from 'nomst/Stores/Decision/payHistoryChangeEntry';
import { IAmount } from 'typings/amount';
import { Format } from 'nomst/Stores/Utility/format';
import { currencyConversion } from 'utils/MobxDataManager/singletons';
import SafeMath from 'common/safeMath';
import './payHistoryChangeEntryDetailsView.scss';

export interface IPayHistoryChangeEntryDetailsViewProps {
    payHistoryChangeEntry: PayHistoryChangeEntry;
    format: Format;
    selectedCurrency?: string;
}

export class PayHistoryChangeEntryDetailsView extends React.Component<IPayHistoryChangeEntryDetailsViewProps> {

    constructor(props: IPayHistoryChangeEntryDetailsViewProps) {
        super(props);

        this.renderPayHistoryAmount = this.renderPayHistoryAmount.bind(this);
    }

    render(): React.ReactNode {
        const { payHistoryChangeEntry } = this.props;

        return (
            <table className="nomst-pay-history-entry-details">
                <tbody>
                    <tr>
                        <th>Previous Base Pay</th>
                        <td>
                            <PayText
                                payText={this.getValueText(payHistoryChangeEntry.previousBasePay || 0)}
                                annualPayText={this.getValueText(payHistoryChangeEntry.annualPreviousBasePay)}
                                isHourly={payHistoryChangeEntry.isHourly}
                                payTypeClassName="previous-base-pay"
                            />
                        </td>
                    </tr>
                    {payHistoryChangeEntry.amounts.map(amount =>
                        amount.updateNewPay ? this.renderPayHistoryAmount(amount) : null)}
                    {this.renderPayHistoryTitleChanges('Previous title', payHistoryChangeEntry.currentTitle)}
                    {this.renderPayHistoryTitleChanges('New title', payHistoryChangeEntry.newTitle)}
                    <tr className="pay-row">
                        <th>Base Pay</th>
                        <td>
                            <PayText
                                payText={this.getValueText(payHistoryChangeEntry.basePay)}
                                annualPayText={this.getValueText(payHistoryChangeEntry.annualBasePay)}
                                isHourly={payHistoryChangeEntry.isHourly}
                                payTypeClassName="base-pay"
                            />
                        </td>
                    </tr>
                    {payHistoryChangeEntry.amounts.map(amount =>
                        amount.updateTcc ? this.renderPayHistoryAmount(amount) : null)}
                    <tr className="pay-row">
                        <th>TCC</th>
                        <td>{this.getValueText(payHistoryChangeEntry.tcc)}</td>
                    </tr>
                    {payHistoryChangeEntry.amounts.map(amount =>
                        (!amount.updateNewPay && !amount.updateTcc) ? this.renderPayHistoryAmount(amount) : null)}
                </tbody>
            </table>
        );
    }

    private renderPayHistoryAmount(amount: IAmount): React.ReactNode {
        const { format } = this.props;
        let valueDisplayText = '';
        if (amount.amountCode === AmountCode.Stock) {
            valueDisplayText = this.getStockText();
        } else {
            const valueText = this.getValueText(amount.value || 0);
            valueDisplayText = amount.percentage && amount.percentage > 0
                ? `${valueText} / ${format.formatPercentage(amount.percentage)}`
                : valueText;
        }

        return (
            <tr key={amount.amountCode}>
                <th>{amount.title}</th>
                <td>
                    <span data-mf-replace-inner="***">{valueDisplayText} </span>
                    {this.renderProration(amount)}
                </td>
            </tr>
        );
    }

    private renderProration(amount: IAmount): React.ReactNode {
        if (amount.isProrated === false) {
            return null;
        }

        const { format } = this.props;
        return (
            <span className="proration">
                Proration <b>{format.formatPercentage(amount.proratedPercentage || 0)}</b>
            </span>
        );
    }

    private renderPayHistoryTitleChanges(label: string, title: Nullable<string>): React.ReactNode {
        if (title === null) {
            return null;
        }

        return (
            <tr className="titles">
                <th>{label}</th>
                <td>{title}</td>
            </tr>
        );
    }

    private getValueText(value: number): string {
        const { payHistoryChangeEntry, selectedCurrency, format } = this.props;
        let convertedValue = value;
        let displayCurrency = payHistoryChangeEntry.isoCurrencyCode;
        if (selectedCurrency) {
            convertedValue =
                currencyConversion.convertCurrency(payHistoryChangeEntry.isoCurrencyCode, selectedCurrency, value);
            displayCurrency = selectedCurrency;
        }
        return format.formatCurrencyValue(displayCurrency, convertedValue, payHistoryChangeEntry.isHourly);
    }

    private getStockText(): string {
        const { format } = this.props;
        const {
            stockPay,
            stockPrice,
            stockUnitType,
            homeIsoCurrencyCode,
            isoCurrencyCode
        } = this.props.payHistoryChangeEntry;
        if (!stockPay || stockPay <= 0) {
            return '';
        }

        if (stockUnitType === StockDisplayType.Unit) {
            return format.formatStockDecimal(stockPay);
        }

        if (!stockPrice) {
            return '';
        }

        const unitValue = SafeMath.divide(
            stockPay,
            currencyConversion.convertCurrency(homeIsoCurrencyCode, isoCurrencyCode, stockPrice)
        );
        return `${this.getValueText(stockPay)} / ${format.formatStockDecimal(unitValue)}`;
    }
}

export default observer(PayHistoryChangeEntryDetailsView);